import { ArrowUpDown, Eye } from 'lucide-react';
import { KTSVG } from '../../../_metronic/helpers';

interface LogTableProps {
  data: any[];
  onViewDetails: (item: any) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  selectedIds?: number[];
  onSelectRow?: (id: number) => void;
  onSelectAll?: (checked: boolean) => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const isoString = date.toISOString();
  const year = isoString.substring(0, 4);
  const month = isoString.substring(5, 7);
  const day = isoString.substring(8, 10);
  const hour = isoString.substring(11, 13);
  const minute = isoString.substring(14, 16);
  const second = isoString.substring(17, 19);
  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
};

const getLogTypeBadgeColor = (type: string): string => {
  const lowerType = type.toLowerCase();
  if (lowerType.includes('erro') || lowerType.includes('error')) return 'danger';
  if (lowerType.includes('sucesso') || lowerType.includes('success')) return 'success';
  if (lowerType.includes('aviso') || lowerType.includes('warning')) return 'warning';
  if (lowerType.includes('info') || lowerType.includes('informação')) return 'info';
  if (lowerType.includes('search') || lowerType.includes('busca')) return 'primary';
  return 'secondary';
};

const LogTable: React.FC<LogTableProps> = ({
  data,
  onViewDetails,
  sortField,
  sortDirection,
  onSort,
  selectedIds = [],
  onSelectRow,
  onSelectAll,
}) => {
  const allSelected = data.length > 0 && data.every((item) => selectedIds.includes(item.id_log));
  const someSelected = data.some((item) => selectedIds.includes(item.id_log)) && !allSelected;
  const SortableHeader = ({
    field,
    children,
  }: {
    field: string;
    children: React.ReactNode;
  }) => (
    <th
      className="min-w-100px text-center cursor-pointer user-select-none"
      onClick={() => onSort(field)}
      style={{ cursor: 'pointer' }}
    >
      <div className="d-flex align-items-center justify-content-center gap-2">
        {children}
        <ArrowUpDown
          size={14}
          className={sortField === field ? 'text-primary' : 'text-muted'}
        />
      </div>
    </th>
  );

  return (
    <div className="table-responsive">
      <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
        <thead>
          <tr className="fw-bold text-muted">
            
            <SortableHeader field="user">Usuário</SortableHeader>
            <th className="min-w-200px text-center">Descrição</th>
            <SortableHeader field="type">Tipo</SortableHeader>
            <SortableHeader field="created_at">Data</SortableHeader>
            <th className="min-w-100px text-center">Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={onSelectAll ? 6 : 5} className="text-center py-10">
                <div className="d-flex flex-column align-items-center">
                  <KTSVG
                    path="/media/icons/duotune/files/fil024.svg"
                    className="svg-icon-5x svg-icon-gray-400 mb-4"
                  />
                  <span className="text-gray-600 fs-5">Nenhum log encontrado</span>
                </div>
              </td>
            </tr>
          ) : (
            data.map((item: any) => {
              const isSelected = selectedIds.includes(item.id_log);
              return (
                <tr
                  key={`${item.id_log}`}
                  className={`hover-elevate-up ${isSelected ? 'bg-light-primary' : ''}`}
                >
                  
                  <td className="text-center">
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="symbol symbol-35px me-2">
                        <div className="symbol-label bg-light-primary text-primary fw-bold fs-7">
                          {item.user?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      </div>
                      <span className="text-dark fw-semibold fs-6">{item.user}</span>
                    </div>
                  </td>
                <td className="text-center">
                  <span className="text-dark fw-normal fs-6">{item.name}</span>
                </td>
                <td className="text-center">
                  <span
                    className={`badge badge-light-${getLogTypeBadgeColor(
                      item.type
                    )} fw-semibold`}
                  >
                    {item.type}
                  </span>
                </td>
                <td className="text-center">
                  <span className="text-dark fw-normal fs-7">
                    {formatDate(item.created_at)}
                  </span>
                </td>
                <td className="text-center">
                  {item.data.toString() !== '{}' ? (
                    item.type.indexOf('search') !== -1 ? (
                      <span className="badge badge-light-primary">
                        {item.json?.search || 'Busca'}
                      </span>
                    ) : (
                      <button
                        className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm action-btn"
                        onClick={() => onViewDetails(item)}
                        title="Ver detalhes"
                      >
                        <Eye size={18} />
                      </button>
                    )
                  ) : (
                    <span className="text-gray-400 fs-7">Sem dados</span>
                  )}
                </td>
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LogTable;
